import { DEFAULT_PARAMS, objectsRHandler } from '@/utils/objectsRHandler';
// eslint-disable-next-line import/no-named-as-default
import Tournament from '../list/tournament/index';
import TournamentStage from '../list/tournament/stage/index';
import TournamentStageGroup from '../list/tournament/stage/group/index';
import { apiService } from '@/services/APIService';
import { rHandler } from '@/plugins/RHandler';
import TournamentStageGroupMember from '../list/tournament/stage/group/member/index';
import TournamentStageGroupMemberHistory from '../list/tournament/stage/group/member/history/index';
import TournamentStageGroupDopScore from '../list/tournament/stage/group/dop-score';
import TournamentStageGroupDopScoreItem from '../list/tournament/stage/group/dop-score/item';
import TournamentStageGroupUserScore from '../list/tournament/stage/group/member/scores';
import TournamentDopScoreTemplate from '../list/tournament/dop-score-template';

const API_PATH = 'tournaments';
const API_PATH_STAGES = 'tournament-stages';
const API_PATH_GROUPS = 'tournament-stage-groups';
const API_PATH_DOP_SCORES = 'tournament-stage-group-dop-scores';
const API_PATH_DOP_SCORE_ITEMS = 'tournament-stage-group-dop-score-items';
const API_PATH_USER_SCORES = 'tournament-stage-group-user-scores';
const API_PATH_DOP_SCORE_TEMPLATES = 'tournament-dop-score-templates';

const getResponseItems = (response) => {
  const { data } = response.data;
  if (Array.isArray(data)) return data;
  if (data?.id) return [data];
  return Object.values(data || {});
};

export const TournamentManager = {
  dopScoreTemplates: {
    index({ params, obs } = DEFAULT_PARAMS) {
      return rHandler(
        apiService.get(API_PATH_DOP_SCORE_TEMPLATES, params),
        (response) => ({
          response,
          items: getResponseItems(response).map((item) => new TournamentDopScoreTemplate(item)),
        }),
        (error) => ({ error }),
        obs,
      );
    },
    create({ data, obs } = DEFAULT_PARAMS) {
      return rHandler(
        apiService.post(API_PATH_DOP_SCORE_TEMPLATES, data),
        (response) => ({ response, item: new TournamentDopScoreTemplate(response.data.data) }),
        (error) => ({ error }),
        obs,
      );
    },
    update({ args = [], data, obs } = DEFAULT_PARAMS) {
      return rHandler(
        apiService.put(`${API_PATH_DOP_SCORE_TEMPLATES}/${args[0]}`, data),
        (response) => ({ response, item: new TournamentDopScoreTemplate(response.data.data) }),
        (error) => ({ error }),
        obs,
      );
    },
    remove({ args = [], obs } = DEFAULT_PARAMS) {
      return rHandler(
        apiService.delete(`${API_PATH_DOP_SCORE_TEMPLATES}/${args[0]}`),
        (response) => ({ response }),
        (error) => ({ error }),
        obs,
      );
    },
    syncItems({ args = [], data, obs } = DEFAULT_PARAMS) {
      return rHandler(
        apiService.put(`${API_PATH_DOP_SCORE_TEMPLATES}/${args[0]}/items`, data),
        (response) => ({
          response,
          items: getResponseItems(response).map((item) => new TournamentStageGroupDopScoreItem(item)),
        }),
        (error) => ({ error }),
        obs,
      );
    },
    async saveConfiguration({ current, data, obs } = DEFAULT_PARAMS) {
      if (!current) {
        return TournamentManager.dopScoreTemplates.create({
          data: {
            event_id: data.event_id,
            nomination_id: data.nomination_id,
            event_group_id: data.event_group_id,
            type: data.type,
            convert_to_100_scale: data.convert_to_100_scale,
            decimal_places: data.decimal_places,
            items: data.items.map((item) => new TournamentStageGroupDopScoreItem(item).toSyncPayload()),
          },
          obs,
        });
      }
      const parentChanged = current.type !== data.type
        || current.convert_to_100_scale !== data.convert_to_100_scale
        || current.decimal_places !== data.decimal_places;
      const itemsChanged = !current.hasSameItems(data.items);
      if (!parentChanged && !itemsChanged) return { item: current };
      let parent = current;
      const syncFirst = data.convert_to_100_scale && !current.convert_to_100_scale && itemsChanged;
      if (syncFirst) {
        const synced = await TournamentManager.dopScoreTemplates.syncItems({
          args: [current.id],
          data: { items: data.items.map((item) => new TournamentStageGroupDopScoreItem(item).toSyncPayload()) },
          obs,
        });
        if (synced.error) return synced;
        parent = new TournamentDopScoreTemplate({ ...current, items: synced.items });
      }
      if (parentChanged) {
        const result = await TournamentManager.dopScoreTemplates.update({
          args: [current.id],
          data: {
            type: data.type,
            convert_to_100_scale: data.convert_to_100_scale,
            decimal_places: data.decimal_places,
          },
          obs,
        });
        if (result.error) return { ...result, partialItem: parent };
        parent = new TournamentDopScoreTemplate({ ...result.item, items: parent.items });
      }
      if (!itemsChanged || syncFirst) return { item: parent };
      const result = await TournamentManager.dopScoreTemplates.syncItems({
        args: [current.id],
        data: { items: data.items.map((item) => new TournamentStageGroupDopScoreItem(item).toSyncPayload()) },
        obs,
      });
      return result.error ? { ...result, partialItem: parent } : {
        ...result,
        item: new TournamentDopScoreTemplate({ ...parent, items: result.items }),
      };
    },
  },
  dopScores: {
    index({ params, obs } = DEFAULT_PARAMS) {
      return rHandler(
        apiService.get(API_PATH_DOP_SCORES, params),
        (response) => ({
          response,
          items: getResponseItems(response)
            .map((item) => new TournamentStageGroupDopScore(item)),
        }),
        (error) => ({ error }),
        obs,
      );
    },
    async getByGroup({ args = [], obs } = DEFAULT_PARAMS) {
      const [groupId] = args;
      const result = await TournamentManager.dopScores.index({
        params: {
          'search[tournament_stage_group_id]': groupId,
          'per-page': -1,
          expand: 'items',
        },
        obs,
      });
      return result.error ? result : { ...result, item: result.items[0] || null };
    },
    create({ data, obs } = DEFAULT_PARAMS) {
      return rHandler(
        apiService.post(API_PATH_DOP_SCORES, data),
        (response) => ({
          response,
          item: new TournamentStageGroupDopScore(response.data.data),
        }),
        (error) => ({ error }),
        obs,
      );
    },
    update({ args = [], data, obs } = DEFAULT_PARAMS) {
      const [id] = args;
      return rHandler(
        apiService.put(`${API_PATH_DOP_SCORES}/${id}`, data),
        (response) => ({
          response,
          item: new TournamentStageGroupDopScore(response.data.data),
        }),
        (error) => ({ error }),
        obs,
      );
    },
    syncItems({ args = [], data, obs } = DEFAULT_PARAMS) {
      const [id] = args;
      return rHandler(
        apiService.put(`${API_PATH_DOP_SCORES}/${id}/items`, data),
        (response) => ({
          response,
          items: response.data.data.map((item) => new TournamentStageGroupDopScoreItem(item)),
        }),
        (error) => ({ error }),
        obs,
      );
    },
    async saveConfiguration({ args = [], data, current, obs } = DEFAULT_PARAMS) {
      const [groupId] = args;
      const convert = data.convert_to_100_scale ?? current?.convert_to_100_scale ?? false;
      const precision = data.decimal_places ?? current?.decimal_places ?? 2;
      const typeChanged = !current?.isServer || current.type !== data.type
        || current.convert_to_100_scale !== convert
        || current.decimal_places !== precision;
      const itemsChanged = !current?.isServer || !current.hasSameItems(data.items);
      if (!typeChanged && !itemsChanged) return { item: current };

      let savedCurrent = current;
      const syncFirst = current?.isServer && convert
        && !current.convert_to_100_scale && itemsChanged;
      if (syncFirst) {
        const synced = await TournamentManager.dopScores.syncItems({
          args: [current.id],
          data: { items: data.items.map((item) => new TournamentStageGroupDopScoreItem(item).toSyncPayload()) },
          obs,
        });
        if (synced.error) return synced;
        savedCurrent = new TournamentStageGroupDopScore({ ...current, items: synced.items });
      }

      let parentResult;
      if (!current?.isServer) {
        parentResult = await TournamentManager.dopScores.create({
          data: {
            tournament_stage_group_id: groupId,
            type: data.type,
            convert_to_100_scale: convert,
            decimal_places: precision,
          },
          obs,
        });
      } else if (typeChanged) {
        parentResult = await TournamentManager.dopScores.update({
          args: [current.id],
          data: {
            type: data.type,
            convert_to_100_scale: convert,
            decimal_places: precision,
          },
          obs,
        });
      } else {
        parentResult = { item: current };
      }
      if (parentResult.error) return { ...parentResult, partialItem: savedCurrent };

      const parent = parentResult.item;
      if (!itemsChanged || syncFirst) {
        return {
          ...parentResult,
          item: new TournamentStageGroupDopScore({ ...parent, items: savedCurrent.items }),
        };
      }

      const itemsResult = await TournamentManager.dopScores.syncItems({
        args: [parent.id],
        data: {
          items: data.items.map((itemData) => (
            new TournamentStageGroupDopScoreItem(itemData).toSyncPayload()
          )),
        },
        obs,
      });
      if (itemsResult.error) {
        return {
          ...itemsResult,
          partialItem: new TournamentStageGroupDopScore({
            ...parent,
            items: current?.items || [],
          }),
        };
      }
      return {
        ...itemsResult,
        item: new TournamentStageGroupDopScore({ ...parent, items: itemsResult.items }),
      };
    },
  },
  dopScoreItems: {
    ...objectsRHandler(API_PATH_DOP_SCORE_ITEMS, TournamentStageGroupDopScoreItem),
    updateOrder({ args = [], data, obs } = DEFAULT_PARAMS) {
      const [id] = args;
      return rHandler(
        apiService.put(`${API_PATH_DOP_SCORE_ITEMS}/${id}/order`, data),
        (response) => ({
          response,
          item: new TournamentStageGroupDopScoreItem(response.data.data),
        }),
        (error) => ({ error }),
        obs,
      );
    },
  },
  userScores: {
    index({ params, obs } = DEFAULT_PARAMS) {
      return rHandler(
        apiService.get(API_PATH_USER_SCORES, params),
        (response) => ({
          response,
          items: getResponseItems(response)
            .map((item) => new TournamentStageGroupUserScore(item)),
        }),
        (error) => ({ error }),
        obs,
      );
    },
    saveBulk({ args = [], data, obs } = DEFAULT_PARAMS) {
      const [groupId] = args;
      return rHandler(
        apiService.post(`${API_PATH_GROUPS}/${groupId}/users/dop-score`, data),
        (response) => ({ response }),
        (error) => ({ error }),
        obs,
      );
    },
  },
  get objects() {
    return {
      ...objectsRHandler(API_PATH, Tournament),
      getUserHistory({ args = [], params } = DEFAULT_PARAMS) {
        const [trounamentId, userId] = args;
        const path = `/${API_PATH}/${trounamentId}/user-history/${userId}`;
        return rHandler(
          apiService.get(path, params),
          (response) => ({
            response,
            item: Object.values(response.data.data).map((i) => new TournamentStageGroupMemberHistory(i, path)),
          }),
          (error) => ({ error }),
        );
      },
      getUserFullHistory({ args = [], params } = DEFAULT_PARAMS) {
        const [trounamentId, userId] = args;
        const path = `/${API_PATH}/${trounamentId}/user-full-history/${userId}`;
        return rHandler(
          apiService.get(path, params),
          (response) => ({
            response,
            items: Object.values(response.data.data).map((i) => new TournamentStageGroupMemberHistory(i, path)),
          }),
          (error) => ({ error }),
        );
      },
    };
  },
  stages: objectsRHandler(API_PATH_STAGES, TournamentStage),
  get groups() {
    return {
      ...objectsRHandler(API_PATH_GROUPS, TournamentStageGroup),
      getMembers({ args = [], params } = DEFAULT_PARAMS) {
        const [id] = args;
        return rHandler(
          apiService.get(`/${API_PATH_GROUPS}/${id}/users`, params),
          (response) => ({
            response,
            items: response.data.data.map((i) => new TournamentStageGroupMember(i, 'users')),
          }),
          (error) => ({ error }),
        );
      },
      addMembers({ args = [], data } = DEFAULT_PARAMS) { // { user_ids: [] },
        const [id] = args;
        return rHandler(
          apiService.post(`/${API_PATH_GROUPS}/${id}/users/add`, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
      removeMembers({ args = [], data } = DEFAULT_PARAMS) { // { user_ids: [] },
        const [id] = args;
        return rHandler(
          apiService.post(`/${API_PATH_GROUPS}/${id}/users/remove`, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
      transferMembers({ args = [], data } = DEFAULT_PARAMS) { // { tournament_stage_group_id: Number, user_ids: [] },
        const [id] = args;
        return rHandler(
          apiService.post(`/${API_PATH_GROUPS}/${id}/transfer-users`, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
      addScores({ args = [], data } = DEFAULT_PARAMS) { // { users: { user_id, score, description?, status? }[] },
        const [id] = args;
        return rHandler(
          apiService.post(`/${API_PATH_GROUPS}/${id}/users/score`, data),
          (response) => ({ response }),
          (error) => ({ error }),
        );
      },
      getTaskMembers({ args = [], params } = DEFAULT_PARAMS) {
        const [groupId, taskId] = args;
        return rHandler(
          apiService.get(`/${API_PATH_GROUPS}/${groupId}/users-poll/${taskId}`, params),
          (response) => ({
            response,
            items: response.data.data.map((i) => new TournamentStageGroupMember(i, 'users')),
          }),
          (error) => ({ error }),
        );
      },
    };
  },
};
